import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Activity,
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  CircleHelp,
  Clock3,
  Cpu,
  Database,
  Gauge,
  HardDrive,
  Loader2,
  RefreshCw,
  Server,
  Wifi,
  WifiOff,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const COLORS = {
  cpu: "#73e0b1",
  memory: "#e6b85c",
  rx: "#73e0b1",
  tx: "#e6b85c",
};

function formatPercent(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return `${Number(value).toFixed(1)}%`;
}

function formatBytes(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  const bytes = Number(value);
  if (bytes < 1024) return `${bytes.toFixed(0)} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 100 ? 0 : 1)} ${units[unit]}`;
}

function formatRate(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return `${formatBytes(Number(value))}/s`;
}

function formatUptime(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  const seconds = Math.max(0, Math.floor(Number(value)));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDateTime(value) {
  if (!value) return "Sem horário";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Horário indisponível";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function formatChartTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function formatAgo(value) {
  if (!value) return "Sem amostra";
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  if (!Number.isFinite(elapsed)) return "Horário indisponível";
  if (elapsed < 60_000) return `há ${Math.max(1, Math.floor(elapsed / 1000))}s`;
  if (elapsed < 3_600_000) return `há ${Math.floor(elapsed / 60_000)} min`;
  if (elapsed < 86_400_000) return `há ${Math.floor(elapsed / 3_600_000)} h`;
  return `há ${Math.floor(elapsed / 86_400_000)} d`;
}

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (["running", "online", "started", "up"].includes(value)) {
    return "border-emerald-400/25 bg-emerald-400/10 text-emerald-300";
  }
  if (["stopped", "offline", "down", "error"].includes(value)) {
    return "border-rose-400/25 bg-rose-400/10 text-rose-300";
  }
  return "border-amber-300/25 bg-amber-300/10 text-amber-200";
}

function PanelLabel({ children, trailing }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="text-[10px] font-mono font-medium uppercase tracking-[0.16em] text-zinc-500">{children}</div>
      {trailing}
    </div>
  );
}

function Metric({ icon: Icon, label, value, hint, tint = "text-zinc-400" }) {
  return (
    <div className="min-w-0 border-l border-white/[0.08] pl-3 first:border-0 first:pl-0">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
        <Icon className={`h-3.5 w-3.5 ${tint}`} />
        <span>{label}</span>
      </div>
      <div className="truncate font-mono text-lg font-medium tracking-tight text-zinc-100">{value}</div>
      {hint && <div className="mt-1 truncate text-[10px] text-zinc-600">{hint}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-white/10 bg-[#111516] px-3 py-2 shadow-xl">
      <div className="mb-1.5 font-mono text-[10px] text-zinc-500">{formatChartTime(label)}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="flex items-center justify-between gap-5 font-mono text-xs">
          <span style={{ color: item.color }}>{item.name}</span>
          <span className="text-zinc-100">{formatter(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ResourceChart({ title, icon: Icon, data, series, formatter, emptyLabel, note }) {
  const hasValues = data.some((point) => series.some(({ key }) => point[key] !== null && point[key] !== undefined));
  return (
    <section className="border border-white/[0.08] bg-[#111516] p-4 sm:p-5">
      <PanelLabel trailing={note && <span className="text-[10px] font-mono text-zinc-600">{note}</span>}>
        <span className="inline-flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{title}</span>
      </PanelLabel>
      {hasValues ? (
        <div className="h-[168px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 2, left: -23, bottom: 0 }}>
              <defs>
                {series.map(({ key, color }) => (
                  <linearGradient key={key} id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke="#27302e" strokeDasharray="3 5" />
              <XAxis
                dataKey="time"
                tickFormatter={formatChartTime}
                tick={{ fill: "#66706d", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
                minTickGap={28}
              />
              <YAxis
                tickFormatter={formatter}
                tick={{ fill: "#66706d", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
                width={46}
              />
              <Tooltip content={<ChartTooltip formatter={formatter} />} />
              {series.map(({ key, name, color }) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={name}
                  stroke={color}
                  strokeWidth={1.8}
                  fill={`url(#fill-${key})`}
                  connectNulls={false}
                  isAnimationActive={false}
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-[168px] items-center justify-center border border-dashed border-white/[0.07] text-center">
          <div className="max-w-[220px]">
            <Activity className="mx-auto mb-2 h-4 w-4 text-zinc-700" />
            <div className="text-xs text-zinc-500">{emptyLabel}</div>
          </div>
        </div>
      )}
    </section>
  );
}

function ProviderSection({ provider }) {
  const nodes = Array.isArray(provider.nodes) ? provider.nodes : [];
  const isConnected = provider.connected === true;
  return (
    <section className="overflow-hidden border border-white/[0.09] bg-[#0f1313]" data-testid={`monitor-provider-${provider.id}`}>
      <div className={`flex flex-wrap items-center justify-between gap-4 border-b border-white/[0.08] px-5 py-4 sm:px-6 ${isConnected ? "bg-emerald-300/[0.025]" : "bg-rose-300/[0.025]"}`}>
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center border ${isConnected ? "border-emerald-300/20 bg-emerald-300/[0.08] text-emerald-200" : "border-rose-300/20 bg-rose-300/[0.08] text-rose-200"}`}>
            {isConnected ? <Server className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <div className="truncate font-heading text-lg font-bold tracking-tight text-zinc-100">{provider.label || provider.id}</div>
            <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              {provider.id || "provider"} <span className="mx-1.5 text-zinc-700">/</span> {nodes.length} {nodes.length === 1 ? "nó" : "nós"}
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${isConnected ? "border-emerald-300/20 text-emerald-200" : "border-rose-300/20 text-rose-200"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${isConnected ? "bg-emerald-300 dot-blink" : "bg-rose-300"}`} />
          {isConnected ? "Conectado" : provider.configured ? "Sem conexão" : "Não configurado"}
        </div>
      </div>

      {!provider.configured && (
        <div className="flex items-start gap-3 px-5 py-5 sm:px-6">
          <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
          <div>
            <div className="text-sm text-zinc-300">Este provedor ainda não está configurado.</div>
            <div className="mt-1 text-xs leading-relaxed text-zinc-600">Nenhuma métrica está disponível para exibição.</div>
          </div>
        </div>
      )}

      {provider.configured && !isConnected && (
        <div className="flex items-start gap-3 px-5 py-5 sm:px-6">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
          <div className="min-w-0">
            <div className="text-sm text-zinc-300">Não foi possível obter dados deste provedor.</div>
            <div className="mt-1 break-words text-xs leading-relaxed text-zinc-500">{provider.error || "A conexão não está disponível no momento."}</div>
          </div>
        </div>
      )}

      {isConnected && nodes.length === 0 && (
        <div className="flex items-start gap-3 px-5 py-5 sm:px-6">
          <Server className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
          <div>
            <div className="text-sm text-zinc-300">Conexão ativa, sem nós retornados.</div>
            <div className="mt-1 text-xs text-zinc-600">A resposta atual não contém nós para monitorar.</div>
          </div>
        </div>
      )}

      {isConnected && nodes.length > 0 && (
        <div className="divide-y divide-white/[0.07]">
          {nodes.map((node) => <NodePanel key={node.name} node={node} providerLabel={provider.label || provider.id} />)}
        </div>
      )}
    </section>
  );
}

function NodePanel({ node, providerLabel }) {
  const history = Array.isArray(node.history)
    ? [...node.history].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
    : [];
  const memory = node.memory || {};
  const network = node.network || {};
  const diskIo = node.disk_io || {};
  const storage = Array.isArray(node.storage) ? node.storage : [];
  const cpuInfo = node.cpu_cores !== null && node.cpu_cores !== undefined
    ? `${node.cpu_cores} ${Number(node.cpu_cores) === 1 ? "core" : "cores"}${node.cpu_threads !== null && node.cpu_threads !== undefined ? ` · ${node.cpu_threads} threads` : ""}`
    : node.cpu_threads !== null && node.cpu_threads !== undefined ? `${node.cpu_threads} threads` : null;

  return (
    <article className="px-4 py-5 sm:px-6" data-testid="monitor-node">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-white/[0.08] bg-white/[0.025] text-zinc-400">
            <HardDrive className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate font-mono text-sm font-semibold text-zinc-100">{node.name || "Nó sem nome"}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-mono text-zinc-600">
              <span>{providerLabel}</span>
              {cpuInfo && <><span className="text-zinc-800">/</span><span>{cpuInfo}</span></>}
              <span className="text-zinc-800">/</span>
              <span>uptime {formatUptime(node.uptime_seconds)}</span>
            </div>
          </div>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1.5 border px-2 py-1 font-mono text-[10px] uppercase tracking-wider ${statusTone(node.status)}`}>
          <span className="h-1 w-1 rounded-full bg-current" />
          {node.status || "Status indisponível"}
        </span>
      </div>

      {node.error && (
        <div className="mb-4 flex items-start gap-2 border border-rose-300/15 bg-rose-300/[0.04] px-3 py-2.5 text-xs text-rose-200/80">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="break-words">{node.error}</span>
        </div>
      )}
      {(node.rrd_error || network.stale || diskIo.stale) && (
        <div className="mb-4 flex items-start gap-2 border border-amber-200/20 bg-amber-200/[0.045] px-3 py-2.5 text-xs text-amber-100/80">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {node.rrd_error || "A última amostra de rede ou I/O está antiga. As taxas atuais ficam indisponíveis até chegar uma nova leitura."}
          </span>
        </div>
      )}

      <div className="mb-5 grid grid-cols-2 gap-4 border border-white/[0.07] bg-[#0b0f0f] px-4 py-3.5 md:grid-cols-3 xl:grid-cols-6 sm:gap-3">
        <Metric icon={Cpu} label="CPU" value={formatPercent(node.cpu_percent)} tint="text-emerald-300" />
        <Metric
          icon={Database}
          label="Memória"
          value={formatPercent(memory.percent)}
          hint={memory.used_bytes != null || memory.total_bytes != null ? `${formatBytes(memory.used_bytes)} / ${formatBytes(memory.total_bytes)}` : null}
          tint="text-amber-200"
        />
        <Metric
          icon={ArrowDownToLine}
          label="Recebido"
          value={formatRate(network.rx_bytes_per_sec)}
          hint={network.stale ? `Amostra antiga ${formatAgo(network.sample_at)}` : network.sample_at ? `Amostra ${formatAgo(network.sample_at)}` : "Sem horário de amostra"}
          tint="text-emerald-300"
        />
        <Metric
          icon={ArrowUpFromLine}
          label="Enviado"
          value={formatRate(network.tx_bytes_per_sec)}
          hint={network.stale ? `Amostra antiga ${formatAgo(network.sample_at)}` : network.sample_at ? `Amostra ${formatAgo(network.sample_at)}` : "Sem horário de amostra"}
          tint="text-amber-200"
        />
        <Metric
          icon={ArrowDownToLine}
          label="Leitura disco"
          value={formatRate(diskIo.read_bytes_per_sec)}
          hint={diskIo.stale ? `Amostra antiga ${formatAgo(diskIo.sample_at)}` : diskIo.sample_at ? `Amostra ${formatAgo(diskIo.sample_at)}` : "Sem horário de amostra"}
          tint="text-sky-300"
        />
        <Metric
          icon={ArrowUpFromLine}
          label="Gravação disco"
          value={formatRate(diskIo.write_bytes_per_sec)}
          hint={diskIo.stale ? `Amostra antiga ${formatAgo(diskIo.sample_at)}` : diskIo.sample_at ? `Amostra ${formatAgo(diskIo.sample_at)}` : "Sem horário de amostra"}
          tint="text-violet-300"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-600">
          <Clock3 className="h-3 w-3" />
          <span>Rede: {network.sample_at ? formatDateTime(network.sample_at) : "sem amostra"}</span>
          <span className="text-zinc-800">/</span>
          <span>I/O: {diskIo.sample_at ? formatDateTime(diskIo.sample_at) : "sem amostra"}</span>
        </div>
        <div className="text-[10px] font-mono text-zinc-700">{history.length} pontos de histórico</div>
      </div>

      <section className="mb-4 border border-white/[0.08] bg-[#111516] p-4 sm:p-5">
        <PanelLabel trailing={<span className="text-[10px] font-mono text-zinc-600">Volumes configurados no Proxmox</span>}>
          Armazenamento
        </PanelLabel>
        {node.storage_error && (
          <div className="mb-3 flex items-start gap-2 border border-rose-300/15 bg-rose-300/[0.04] px-3 py-2.5 text-xs text-rose-200/80">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{node.storage_error}</span>
          </div>
        )}
        {storage.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2">
            {storage.map((volume) => {
              const percent = volume.percent == null ? null : Math.max(0, Math.min(100, Number(volume.percent)));
              return (
                <div key={`${volume.name}-${volume.type}`} className="border border-white/[0.07] bg-[#0b0f0f] p-3.5">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs font-medium text-zinc-200" title={volume.name}>{volume.name}</div>
                      <div className="mt-1 text-[10px] font-mono text-zinc-600">{volume.type}{volume.shared ? " · compartilhado" : ""}</div>
                    </div>
                    <span className={`shrink-0 border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${volume.active && volume.enabled ? "border-emerald-300/20 text-emerald-200" : "border-amber-300/20 text-amber-200"}`}>
                      {volume.active && volume.enabled ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <div
                    className="h-1.5 overflow-hidden bg-white/[0.07]"
                    role="progressbar"
                    aria-label={`Uso do armazenamento ${volume.name}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent ?? undefined}
                  >
                    <div
                      className={`h-full transition-[width] duration-500 ${percent >= 90 ? "bg-rose-300" : percent >= 75 ? "bg-amber-200" : "bg-emerald-300"}`}
                      style={{ width: percent == null ? "0%" : `${percent}%` }}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap justify-between gap-x-3 gap-y-1 font-mono text-[10px]">
                    <span className="text-zinc-300">{percent == null ? "Uso indisponível" : `${percent.toFixed(1)}% usado`}</span>
                    <span className="text-zinc-600">{formatBytes(volume.used_bytes)} de {formatBytes(volume.total_bytes)}</span>
                    <span className="w-full text-zinc-700">Livre: {formatBytes(volume.available_bytes)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-dashed border-white/[0.07] px-4 py-6 text-center text-xs text-zinc-500">
            {node.storage_error ? "Os dados de armazenamento estão indisponíveis." : "Nenhum armazenamento foi retornado para este nó."}
          </div>
        )}
        <p className="mt-3 text-[10px] leading-relaxed text-zinc-700">
          Estes valores mostram o espaço dos volumes/pools apresentados pelo Proxmox, não a saúde SMART dos discos físicos.
        </p>
      </section>

      <div className="grid gap-3 lg:grid-cols-2">
        <ResourceChart
          title="CPU · memória"
          icon={Gauge}
          data={history}
          series={[
            { key: "cpu_percent", name: "CPU", color: COLORS.cpu },
            { key: "memory_percent", name: "Memória", color: COLORS.memory },
          ]}
          formatter={(value) => formatPercent(value)}
          emptyLabel="Sem histórico de CPU ou memória nesta resposta."
        />
        <ResourceChart
          title="Tráfego de rede"
          icon={Wifi}
          data={history}
          series={[
            { key: "rx_bytes_per_sec", name: "Recebido", color: COLORS.rx },
            { key: "tx_bytes_per_sec", name: "Enviado", color: COLORS.tx },
          ]}
          formatter={(value) => formatRate(value)}
          emptyLabel="Sem amostras históricas de tráfego nesta resposta."
          note="RRD pode atualizar em intervalos maiores"
        />
        <ResourceChart
          title="I/O de armazenamento"
          icon={HardDrive}
          data={history}
          series={[
            { key: "disk_read_bytes_per_sec", name: "Leitura", color: "#72c9f5" },
            { key: "disk_write_bytes_per_sec", name: "Gravação", color: "#c6a5ff" },
          ]}
          formatter={(value) => formatRate(value)}
          emptyLabel="Sem amostras históricas de I/O de disco nesta resposta."
          note="RRD pode atualizar em intervalos maiores"
        />
      </div>
    </article>
  );
}

export default function AdminMonitoring() {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [lastSuccessAt, setLastSuccessAt] = useState(null);
  const refreshInterval = useRef(10);
  const inFlight = useRef(false);

  const loadResources = useCallback(async (manual = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (manual) setRefreshing(true);
    try {
      const response = await api.get("/admin/monitoring/resources", { timeout: 30_000 });
      const payload = response.data;
      setResources(payload);
      setRequestError("");
      setLastSuccessAt(new Date().toISOString());
      const seconds = Number(payload?.refresh_interval_seconds);
      if (Number.isFinite(seconds) && seconds > 0) refreshInterval.current = Math.min(Math.max(seconds, 3), 300);
    } catch (error) {
      setRequestError(formatApiError(error));
    } finally {
      inFlight.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    let timer;
    const poll = async () => {
      await loadResources(false);
      if (active) timer = window.setTimeout(poll, refreshInterval.current * 1000);
    };
    poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [loadResources]);

  const providers = Array.isArray(resources?.providers) ? resources.providers : [];
  const connectedCount = providers.filter((provider) => provider.connected === true).length;
  const nodeCount = providers.reduce((total, provider) => total + (Array.isArray(provider.nodes) ? provider.nodes.length : 0), 0);
  const refreshSeconds = Number(resources?.refresh_interval_seconds) || refreshInterval.current;
  const snapshotTime = resources?.sampled_at || lastSuccessAt;

  return (
    <div className="space-y-6 sm:space-y-7" data-testid="admin-monitoring-page">
      <header className="relative overflow-hidden border border-white/[0.09] bg-[#101414]">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[42%] opacity-40" aria-hidden="true">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#101414_0%,rgba(16,20,20,0.3)_70%),linear-gradient(rgba(115,224,177,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(115,224,177,0.08)_1px,transparent_1px)] bg-[size:auto,28px_28px,28px_28px]" />
          <div className="absolute right-12 top-1/2 h-28 w-28 -translate-y-1/2 rounded-full border border-emerald-200/10" />
          <div className="absolute right-[4.6rem] top-1/2 h-20 w-20 -translate-y-1/2 rounded-full border border-emerald-200/10" />
        </div>
        <div className="relative flex flex-col justify-between gap-6 p-5 sm:flex-row sm:items-end sm:p-7">
          <div>
            <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-200/70">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 dot-blink" />
              Flix VPS <span className="text-zinc-700">/</span> infraestrutura
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">Monitor Proxmox</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-zinc-500">
              CPU, memória, rede e armazenamento dos nós Brasil e Paris. O painel mostra o horário real da última amostra de rede e I/O.
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadResources(true)}
            disabled={refreshing}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 border border-white/15 bg-white/[0.035] px-4 font-mono text-xs text-zinc-200 transition-colors hover:border-emerald-200/30 hover:bg-emerald-200/[0.06] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="monitor-refresh"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Atualizar agora
          </button>
        </div>
        <div className="relative grid grid-cols-2 border-t border-white/[0.08] sm:grid-cols-4">
          <div className="border-r border-white/[0.08] px-5 py-3.5 sm:px-7">
            <div className="mb-1 text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-600">Provedores online</div>
            <div className="font-mono text-base text-zinc-100">{connectedCount}<span className="text-zinc-600"> / {providers.length}</span></div>
          </div>
          <div className="border-b border-white/[0.08] px-5 py-3.5 sm:border-b-0 sm:border-r sm:px-7">
            <div className="mb-1 text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-600">Nós retornados</div>
            <div className="font-mono text-base text-zinc-100">{nodeCount}</div>
          </div>
          <div className="border-r border-white/[0.08] px-5 py-3.5 sm:px-7">
            <div className="mb-1 text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-600">Intervalo de atualização</div>
            <div className="font-mono text-base text-zinc-100">{refreshSeconds}s</div>
          </div>
          <div className="px-5 py-3.5 sm:px-7">
            <div className="mb-1 text-[9px] font-mono uppercase tracking-[0.15em] text-zinc-600">Última resposta</div>
            <div className="font-mono text-xs text-zinc-300">{snapshotTime ? formatDateTime(snapshotTime) : "Aguardando dados"}</div>
          </div>
        </div>
      </header>

      {requestError && resources && (
        <div className="flex flex-wrap items-center justify-between gap-3 border border-amber-200/20 bg-amber-200/[0.045] px-4 py-3">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
            <div>
              <div className="text-xs font-medium text-amber-100">Falha na atualização. Exibindo a última resposta recebida.</div>
              <div className="mt-1 break-words text-[11px] text-amber-100/60">{requestError}</div>
            </div>
          </div>
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-amber-100/50">Dados podem estar desatualizados</span>
        </div>
      )}

      {loading && !resources && (
        <div className="space-y-4" aria-label="Carregando recursos">
          {[0, 1].map((item) => (
            <div key={item} className="animate-pulse border border-white/[0.08] bg-[#0f1313] p-5">
              <div className="mb-6 h-9 w-48 bg-white/[0.06]" />
              <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[0, 1, 2, 3].map((metric) => <div key={metric} className="h-16 bg-white/[0.035]" />)}
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {[0, 1].map((chart) => <div key={chart} className="h-52 bg-white/[0.035]" />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !resources && requestError && (
        <div className="border border-rose-300/20 bg-[#111313] px-6 py-12 text-center">
          <AlertCircle className="mx-auto mb-3 h-6 w-6 text-rose-300" />
          <h2 className="font-heading text-lg font-semibold text-zinc-100">Monitor indisponível</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-zinc-500">{requestError}</p>
          <button
            type="button"
            onClick={() => loadResources(true)}
            className="mt-5 inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs text-zinc-200 hover:bg-white/[0.05]"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Tentar novamente
          </button>
        </div>
      )}

      {resources && providers.length === 0 && (
        <div className="border border-white/[0.08] bg-[#0f1313] px-6 py-12 text-center">
          <Server className="mx-auto mb-3 h-6 w-6 text-zinc-600" />
          <h2 className="font-heading text-lg font-semibold text-zinc-200">Nenhum provedor retornado</h2>
          <p className="mt-2 text-sm text-zinc-500">A resposta do monitor não contém provedores para exibir.</p>
        </div>
      )}

      {resources && providers.length > 0 && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600">Instâncias selecionadas <span className="mx-1.5 text-zinc-800">/</span> Brasil + Paris</div>
            <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-600">
              <Check className="h-3 w-3 text-emerald-300/70" />
              Atualização automática a cada {refreshSeconds}s
            </div>
          </div>
          {providers.map((provider) => <ProviderSection key={provider.id} provider={provider} />)}
        </div>
      )}
    </div>
  );
}